import { useState } from "react";

import { useToast } from "../../../hooks/useToast";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";
import { ModalBodyText, TranslationInput } from "./AdminTranslations.styles";

interface IAddMasterKeyModalProps {
  usedLocations: string[];
  isSaving: boolean;
  onAdd: (key: string, value: string, usedLocation: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Add a key to the master translations store — Retool's `modalAddKey`
 * (EN-5178). Retool hard-wired the language to en-GB (its Codes field was a
 * disabled input): a key is born as its en-GB value, and other languages are
 * added afterwards per translation. Same here.
 */
export function AddMasterKeyModal({
  usedLocations,
  isSaving,
  onAdd,
  onClose,
}: IAddMasterKeyModalProps) {
  const { showToast } = useToast();

  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [usedLocation, setUsedLocation] = useState("");

  const canSubmit = key.trim() !== "" && value.trim() !== "" && usedLocation;

  const handleAdd = async () => {
    if (!canSubmit) return;

    try {
      await onAdd(key.trim(), value, usedLocation);
      showToast("The key was added successfully.", "success");
      onClose();
    } catch (error) {
      showToast(
        serverErrorMessage(error) ?? "There was an error adding this key.",
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Add key</ModalTitle>

      <ModalForm>
        <ModalBodyText>
          Creates the key with its <strong>en-GB</strong> value. Add other
          languages from the key&apos;s translations afterwards.
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="master-key-key">Key</FilterLabel>
          <FilterInput
            id="master-key-key"
            type="text"
            placeholder="e.g. mobile.home.title"
            value={key}
            disabled={isSaving}
            onChange={(e) => setKey(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="master-key-value">Value (en-GB)</FilterLabel>
          <TranslationInput
            id="master-key-value"
            rows={3}
            value={value}
            disabled={isSaving}
            onChange={(e) => setValue(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="master-key-location">Location</FilterLabel>
          <AdminSelectField
            id="master-key-location"
            value={usedLocation}
            disabled={isSaving}
            onChange={(e) => setUsedLocation(e.target.value)}
          >
            <option value="">Select a location</option>
            {usedLocations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>
            Where the key is consumed (mobile app, API, surveys, ...).
          </FieldHint>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={!canSubmit || isSaving}
            onClick={handleAdd}
          >
            {isSaving ? "Adding..." : "Add key"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
